import { Fragment, useEffect } from 'react'
import type { ReactNode } from 'react'
import {
  Box,
  Link,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import { Link as RouterLink } from 'react-router-dom'
import BrandLogo from '../components/BrandLogo'
import LegalFooter from '../components/LegalFooter'
import { colors } from '../theme'
import type { LegalBlock, LegalDocument, LegalText } from '../content/legal'

// Body text holds to a readable measure; tables use the full width of the sheet.
const MEASURE = '72ch'

// Width at which a table's columns have room to sit side by side.
const TABLE_MIN_WIDTH = 768

function Runs({ text }: { text: LegalText }) {
  if (typeof text === 'string') return <>{text}</>
  return (
    <>
      {text.map((run, i) => {
        let node: ReactNode = run.text
        if (run.italic) node = <em>{node}</em>
        if (run.bold) node = <strong>{node}</strong>
        return <Fragment key={i}>{node}</Fragment>
      })}
    </>
  )
}

function Block({ block }: { block: LegalBlock }) {
  switch (block.type) {
    case 'title':
      return (
        <Typography variant="h5" component="p" sx={{ mt: 3, mb: 1, maxWidth: MEASURE }}>
          {block.text}
        </Typography>
      )
    case 'heading':
      return (
        <Typography variant="subtitle1" component="h2" sx={{ mt: 4, mb: 1, maxWidth: MEASURE }}>
          {block.text}
        </Typography>
      )
    case 'subheading':
      return (
        <Typography variant="subtitle2" component="h3" sx={{ mt: 3, mb: 0.75, maxWidth: MEASURE }}>
          {block.text}
        </Typography>
      )
    case 'paragraph':
      return (
        <Typography
          variant="body1"
          sx={{ mb: 1.5, maxWidth: MEASURE, lineHeight: 1.6, '& strong': { fontWeight: 600 } }}
        >
          <Runs text={block.text} />
        </Typography>
      )
    case 'bullets':
    case 'numbered':
      return (
        <Box
          component={block.type === 'bullets' ? 'ul' : 'ol'}
          sx={{ m: 0, mb: 1.5, pl: 3, maxWidth: MEASURE }}
        >
          {block.items.map((item, i) => (
            <Typography
              key={i}
              component="li"
              variant="body1"
              sx={{ mb: 0.75, lineHeight: 1.6, '& strong': { fontWeight: 600 } }}
            >
              <Runs text={item} />
            </Typography>
          ))}
        </Box>
      )
    case 'callout':
      return (
        <Paper
          variant="outlined"
          sx={{ p: 2, mb: 2, bgcolor: colors.gray50, maxWidth: MEASURE }}
        >
          {/* pre-line keeps a "\n" inside a callout as a line break. */}
          <Typography
            variant="body1"
            sx={{ lineHeight: 1.6, whiteSpace: 'pre-line', '& strong': { fontWeight: 600 } }}
          >
            <Runs text={block.text} />
          </Typography>
        </Paper>
      )
    case 'table':
      return (
        <>
        {/* Below the tablet width the last column would sit behind a sideways
            scroll, and it is the one that matters, so each row becomes a card. */}
        <Box
          component="ul"
          sx={{
            listStyle: 'none',
            m: 0,
            mt: 1,
            mb: 2,
            p: 0,
            display: 'flex',
            flexDirection: 'column',
            gap: 1.5,
            [`@media (min-width:${TABLE_MIN_WIDTH}px)`]: { display: 'none' },
          }}
        >
          {block.rows.map((row, r) => (
            <Paper key={r} component="li" variant="outlined" sx={{ p: 2 }}>
              <Typography variant="subtitle2" component="p" sx={{ mb: 1 }}>
                <Runs text={row[0]} />
              </Typography>
              {row.slice(1).map((cell, c) => (
                <Box key={c} sx={{ mt: c === 0 ? 0 : 1 }}>
                  <Typography variant="caption" component="p" color="text.secondary">
                    {block.head[c + 1]}
                  </Typography>
                  <Typography variant="body1" sx={{ lineHeight: 1.5 }}>
                    <Runs text={cell} />
                  </Typography>
                </Box>
              ))}
            </Paper>
          ))}
        </Box>
        <TableContainer
          component={Paper}
          variant="outlined"
          sx={{
            mt: 1,
            mb: 2,
            display: 'none',
            [`@media (min-width:${TABLE_MIN_WIDTH}px)`]: { display: 'block' },
          }}
        >
          <Table size="small">
            <TableHead>
              <TableRow>
                {block.head.map((cell, i) => (
                  <TableCell key={i} sx={{ verticalAlign: 'top', fontWeight: 600 }}>
                    {cell}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {block.rows.map((row, r) => (
                <TableRow key={r}>
                  {row.map((cell, c) => (
                    <TableCell key={c} sx={{ verticalAlign: 'top', lineHeight: 1.5 }}>
                      <Runs text={cell} />
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
        </>
      )
  }
}

/**
 * A public, read-only page for one legal document. It sits outside the auth
 * guard because prospective clients read these before they have an account, so
 * it carries its own minimal chrome rather than the staff or portal shell.
 */
export default function LegalDocumentPage({ document: doc }: { document: LegalDocument }) {
  useEffect(() => {
    const previous = window.document.title
    window.document.title = `${doc.title} | MissFit Mindset`
    return () => {
      window.document.title = previous
    }
  }, [doc.title])

  return (
    <Box sx={{ minHeight: '100vh', display: 'flex', flexDirection: 'column', bgcolor: 'background.default' }}>
      <Box
        component="header"
        sx={{ bgcolor: 'background.paper', borderBottom: `1px solid ${colors.gray200}` }}
      >
        <Box sx={{ maxWidth: 760, mx: 'auto', px: { xs: 2, sm: 3 }, py: 1.5 }}>
          <Link component={RouterLink} to="/" aria-label="MissFit Mindset home" sx={{ display: 'inline-block' }}>
            <BrandLogo height={32} alt="" />
          </Link>
        </Box>
      </Box>

      <Box
        component="main"
        sx={{ flex: 1, width: '100%', maxWidth: 760, mx: 'auto', px: { xs: 2, sm: 3 }, py: { xs: 3, sm: 5 } }}
      >
        <Paper variant="outlined" sx={{ p: { xs: 2.5, sm: 5 } }}>
          <Typography variant="h4" component="h1" sx={{ mb: 1.5 }}>
            {doc.title}
          </Typography>
          {doc.blocks.map((block, i) => (
            <Block key={i} block={block} />
          ))}
        </Paper>
      </Box>

      <Box
        component="footer"
        sx={{
          bgcolor: 'background.paper',
          borderTop: `1px solid ${colors.gray200}`,
          px: 2,
          py: 1.5,
        }}
      >
        <LegalFooter />
      </Box>
    </Box>
  )
}
